/**
 * NTP-lite clock-offset estimation (PRD §5).
 *
 * A probe round-trip yields four timestamps:
 *   t0          — client send time            (client clock)
 *   serverRecvMs — server receive time         (server clock)
 *   serverSendMs — server send time            (server clock)
 *   t3          — client receive time          (client clock)
 *
 * offset = ((serverRecv - t0) + (serverSend - t3)) / 2   // add to client clock → server time
 * rtt    = (t3 - t0) - (serverSend - serverRecv)         // minus server processing time
 */
export interface TimeSample {
  t0: number;
  serverRecvMs: number;
  serverSendMs: number;
  t3: number;
}

export interface OffsetEstimate {
  /** Add to a client wall-clock reading to get server wall-clock. */
  offsetMs: number;
  rttMs: number;
}

export function estimateOffset(s: TimeSample): OffsetEstimate {
  const offsetMs = ((s.serverRecvMs - s.t0) + (s.serverSendMs - s.t3)) / 2;
  const rttMs = (s.t3 - s.t0) - (s.serverSendMs - s.serverRecvMs);
  return { offsetMs, rttMs };
}

/**
 * Accumulates probe samples and keeps the lowest-RTT one — the sample least
 * polluted by queueing/jitter gives the cleanest offset (PRD §5: "keep the
 * lowest-RTT sample"). Run a few probes on join, then periodically.
 */
export class OffsetEstimator {
  private best: OffsetEstimate | null = null;

  add(sample: TimeSample): OffsetEstimate {
    const est = estimateOffset(sample);
    if (this.best === null || est.rttMs < this.best.rttMs) {
      this.best = est;
    }
    return est;
  }

  get hasEstimate(): boolean {
    return this.best !== null;
  }

  /** Best (lowest-RTT) offset so far; 0 before any sample. */
  get offsetMs(): number {
    return this.best?.offsetMs ?? 0;
  }

  /** RTT of the best sample; Infinity before any sample. */
  get rttMs(): number {
    return this.best?.rttMs ?? Infinity;
  }

  /** Project a client wall-clock reading onto the server's clock. */
  estimatedServerNow(localNowMs: number): number {
    return localNowMs + this.offsetMs;
  }

  /** Forget samples (e.g. after a reconnect to a possibly different server). */
  reset(): void {
    this.best = null;
  }
}
